import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { isOnboardingExemptPath } from '../utils/onboardingExemptPaths';

/**
 * Forces a user who has never completed onboarding's identity step back
 * into /register's step machine, instead of leaving them to wander an
 * org-less app relying only on graceful-failure fallbacks (2026-07-28 Yixil
 * incident, docs/reference/bug-registry.md — that fix made the *aftermath*
 * graceful; this is the "must finish" enforcement it explicitly deferred).
 *
 * Spec 289: onboarding collection was consolidated onto /register (the old
 * /onboard role-fork wizard, Spec 193, is deleted); this hook now sends an
 * incomplete user to /register instead.
 *
 * Gated on the SINGULAR `organization`, not `organizations.length`:
 * the personal-org creation path calls `selectOrganization(org)` (which sets
 * `organization` synchronously) before navigating to /helm, but does NOT
 * push into the plural `organizations` array (that only updates via
 * refreshOrganizations()). Gating on the plural array would bounce a user
 * who just finished onboarding straight back into it. `organization` mirrors
 * the same signal useRequireOrganization already uses for this reason.
 *
 * Also gated on organizationsLoaded, same as useRequireOrganization: without
 * it, a legitimate multi-org user landing mid session-refresh (organization
 * still null while the async fetch resolves) would get bounced into
 * /register before their real org even loads.
 *
 * Checking `user.onboardingIdentityCompletedAt` (not just "has zero orgs")
 * matters for a user who completed onboarding once and later left their only
 * organization — they're org-less but not onboarding-incomplete, and should
 * hit the existing org-less banner/redirect, not get force-walked through
 * the wizard again.
 */
export function useRequireOnboarding(): void {
  const { user } = useAuth();
  const { organization, organizationsLoaded } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    // pending-deletion cancel flow (2026-09-24): PrivateRoute renders ONLY
    // PendingDeletionPage for a pending-deletion user, but this hook runs
    // unconditionally alongside it (rules of hooks) and would otherwise
    // navigate('/register') — an UNGATED public route — out from under that
    // screen for a pending-deletion user who never finished onboarding,
    // bypassing the "cancel or sign out only" restriction entirely.
    if (user.accountStatus === 'pending_deletion') return;
    if (!organizationsLoaded) return;
    if (organization) return;
    if (user.onboardingIdentityCompletedAt) return;
    if (isOnboardingExemptPath(location.pathname)) return;

    navigate('/register', { replace: true });
  }, [user, organization, organizationsLoaded, location.pathname, navigate]);
}
